import React, { Fragment, useState, forwardRef, LegacyRef } from 'react'
import styles from './uploadImage.module.scss'
import i18n from 'constants/strings'
import ImageUploading, { ImageListType, ImageType } from 'react-images-uploading'
import { UploadPlaceholder } from 'components/Icons'
import BtnSecondary from 'components/generic/BtnSecondary'
import BtnPrimary from 'components/generic/BtnPrimary'
import InfoBox from 'components/generic/InfoBox'
import AvatarEditor from 'react-avatar-editor'
import { Range, getTrackBackground } from 'react-range'
import { ReactComponent as PlusIcon } from 'images/icons/icn_add_circle.svg'
import { ReactComponent as MinusIcon } from 'images/icons/icn_minus_circle.svg'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'
import { AccountDetails } from 'types/global'

const UPLOAD_AVATAR_FRAME_WIDTH = 248

const SCALE_MAP: Record<number, number> = {
  1: 1,
  2: 1.2,
  3: 1.4,
  4: 1.6,
  5: 1.8,
  6: 2,
  7: 2.2,
  8: 2.4,
  9: 2.6,
  10: 2.8,
}

interface Props {
  loadedImages: ImageType[]
  setLoadedImages: (images: ImageType[]) => void
  currentAvatarUrl: string | null
}

const UploadImage = forwardRef(
  ({ loadedImages, setLoadedImages, currentAvatarUrl }: Props, ref: LegacyRef<AvatarEditor>) => {
    const [scale, setScale] = useState<number>(1)
    const canZoomIn = scale < 10
    const canZoomOut = scale > 1

    const onChange = async (imageList: ImageListType) => {
      const [image] = imageList

      if (image?.file) {
        setLoadedImages([image] as never[])
      }

      if (!imageList.length) {
        setLoadedImages([])
      }
    }

    return (
      <>
        <ImageUploading
          value={loadedImages}
          onChange={onChange}
          maxNumber={1}
          acceptType={['jpg', 'jpeg', 'png', 'heic', 'heif']}
          allowNonImageType={true}
          maxFileSize={4000000}
        >
          {({ imageList, onImageUpload, onImageUpdate, isDragging, dragProps, errors }) => (
            <div className={styles.imageUploadContainer}>
              {!imageList.length && (
                <div>
                  {currentAvatarUrl && (
                    <span className={styles.dragBelow}>
                      {i18n.t('strUploadImage.dragAndDropBelow')}
                    </span>
                  )}
                  {errors ? (
                    <InfoBox textOnly iconColor='red' boxColor='red'>
                      {errors.acceptType && (
                        <span className={styles.fileUploadErrorText}>
                          {i18n.t('strUploadImage.fileTypeError.1')}
                          <br />
                          {i18n.t('strUploadImage.fileTypeError.2')}
                        </span>
                      )}
                      {errors.maxFileSize && (
                        <span className={styles.fileUploadErrorText}>
                          {i18n.t('strUploadImage.fileSizeError.1')}
                          <br />
                          {i18n.t('strUploadImage.fileSizeError.2')}
                        </span>
                      )}
                    </InfoBox>
                  ) : (
                    <p className={styles.fileUploadInfo}>
                      {i18n.t('strUploadImage.fileUploadText')}
                    </p>
                  )}
                </div>
              )}

              {imageList.length ? (
                imageList.map((image, index) => (
                  <Fragment key={index}>
                    <span className={styles.dragToReposition}>
                      {i18n.t('strUploadImage.dragToReposition')}
                    </span>

                    <div key={index} className={styles.imageUploaded}>
                      <div className={styles.avatarFrameContainer}>
                        <AvatarEditor
                          ref={ref}
                          image={image.dataURL as string}
                          height={UPLOAD_AVATAR_FRAME_WIDTH}
                          width={UPLOAD_AVATAR_FRAME_WIDTH}
                          border={0}
                          scale={SCALE_MAP[scale]}
                          borderRadius={UPLOAD_AVATAR_FRAME_WIDTH / 2}
                          color={[255, 255, 255, 0.6]}
                        />
                      </div>
                      <div className={styles.rangeContainer}>
                        <MinusIcon
                          className={styles.minusIcon}
                          onClick={canZoomOut ? () => setScale(scale - 1) : undefined}
                        />
                        <Range
                          values={[scale]}
                          step={1}
                          min={1}
                          max={10}
                          onChange={values => {
                            const [value] = values
                            setScale(value)
                          }}
                          renderTrack={({ props, children }) => (
                            <div
                              onMouseDown={props.onMouseDown}
                              onTouchStart={props.onTouchStart}
                              className={styles.renderTrackContainer}
                            >
                              <div
                                ref={props.ref}
                                className={styles.renderTrackItem}
                                style={{
                                  background: getTrackBackground({
                                    min: 1,
                                    max: 10,
                                    values: [scale],
                                    colors: ['#003a70', '#ebedef'],
                                  }),
                                }}
                              >
                                {children}
                              </div>
                            </div>
                          )}
                          renderThumb={({ props }) => (
                            <div {...props} className={styles.renderThumb} />
                          )}
                        />
                        <PlusIcon
                          className={styles.plusIcon}
                          onClick={canZoomIn ? () => setScale(scale + 1) : undefined}
                        />
                      </div>
                    </div>

                    <div className={styles.btnsWrapper}>
                      <BtnPrimary
                        border={false}
                        size={'small'}
                        onClick={() => onImageUpdate(index)}
                      >
                        {i18n.t('strUploadImage.uploadAnotherPhoto')}
                      </BtnPrimary>
                    </div>
                  </Fragment>
                ))
              ) : (
                <>
                  {!currentAvatarUrl ? (
                    <div
                      className={`${styles.dragAndDrop} ${isDragging ? styles.isDragging : ''}`}
                      onClick={onImageUpload}
                    >
                      <span {...dragProps} className={styles.dropArea}></span>
                      <UploadPlaceholder />
                      <span className={styles.dragAndDropText}>
                        {i18n.t('strUploadImage.dragAndDropHere')}
                      </span>
                    </div>
                  ) : (
                    <div
                      className={`${styles.dragAndDropFilled} ${
                        isDragging ? styles.isDragging : ''
                      }`}
                      onClick={onImageUpload}
                    >
                      <span {...dragProps} className={styles.dropArea}></span>
                      <img src={currentAvatarUrl} alt='avatar' className={styles.currentAvatar} />
                    </div>
                  )}

                  <div className={styles.orDivider}>
                    <span className={styles.line}></span>
                    <span>or</span>
                    <span className={styles.line}></span>
                  </div>
                  <BtnSecondary size={'small'} onClick={onImageUpload}>
                    {!currentAvatarUrl
                      ? i18n.t('strUploadImage.uploadFromDevice')
                      : i18n.t('strUploadImage.changePhoto')}
                  </BtnSecondary>
                </>
              )}
            </div>
          )}
        </ImageUploading>
      </>
    )
  }
)

export default UploadImage
